import {getCookie, setCookie} from "../../utils/cookieHandler";
import getUUId from "../../utils/uuid";
import CartContract from "./contracts/cartContract";
import FunctionHandler from "../../utils/functionHandler";
import {useEffect, useState} from "react";
import ProductManager from "../productsManager/productsManager";
import {getAuth, onAuthStateChanged} from "firebase/auth";
import hash from 'object-hash';
import ShipmentContract from "./contracts/shipmentContract";
import {getDatabase, onValue, ref} from "firebase/database";
import app from "../../firebaseConfig/firebaseConfig";
import ParamTypes from "../../productParamHandler/paramTypes";
import UserManager from "../userManager/userManager";
import {validateInvoiceData} from "../../checkout/components/billingData/UserInfo";

const db = getDatabase(app);
const shippingPriceRef = ref(db, '/shippingPrices');


class _CartManager {
    constructor() {
        this.cartChangeListeners = {};
        this.cartId = getCookie('cartId') || getUUId();
        this.cart = new CartContract(this.cartId);
        this.shippingPrices = {};
        setCookie('cartId', this.cartId, 7);
        this.user = null;
        this.setCartTimer = null;
        const auth = getAuth();
        onAuthStateChanged(auth, this.onAuth);
        onValue(shippingPriceRef, snapshot => this.updateShippingPrices(snapshot.val()))
    }

    updateShippingPrices = (prices) => {
        this.shippingPrices = prices;
        if (this.cart.shipping) {
            this.cart.shipping.price = this.shippingPrices[this.cart.shipping.type] || 0;
        }
    }

    onAuth = (user) => {
        if (user) {
            this.user = user;
        } else if (this.user) {
            this.user = null;
            this.cartId = getUUId();
            this.cart = new CartContract(this.cartId);
            setCookie('cartId', this.cartId, 7);
        }

        this.loadCart();
    }

    callCartListeners = () => {
        Object.keys(this.cartChangeListeners).forEach(key => {
            this.cartChangeListeners[key](Object.assign({}, this.cart));
        })
    }

    loadCart = () => {
        FunctionHandler.getData(`/getCart/${this.cartId}`, (cart, err) => {
            if (err) {
                this.setCart();
                return;
            }

            if (cart) {
                this.storeCartFromBackend(cart);
                if (!cart.uid && this.user) {
                    this.setCart();
                }
            } else {
                this.setCart();
            }
        });
    }

    storeCartFromBackend = (cart) => {
        this.cart = this.decodeCart(cart);
        this.cartId = cart.cartId;
        this.callCartListeners();
    }

    setDesigns = (designs) => {
        this.cart.designs = designs;
        this.setCart();
    }

    decodeCart = (cart) => {
        const decodedCart = Object.assign(new CartContract(''), cart);
        decodedCart.selectedDesigns = (cart.selectedDesigns || []).reduce((acc, item) => {
            acc[item.key] = item.value;
            return acc;
        }, {});

        return decodedCart;
    }

    encodeCart = (cart) => {
        const encodedCart = Object.assign(new CartContract(''), cart);
        encodedCart.selectedDesigns = [];
        Object.keys(cart.selectedDesigns || {}).forEach(key =>
            encodedCart.selectedDesigns.push({key, value: cart.selectedDesigns[key]})
        );

        if (this.user?.uid) {
            encodedCart.uid = this.user.uid;
        }

        return encodedCart;
    }

    setCartWithTimer = () => {
        if (this.setCartTimer) {
            clearTimeout(this.setCartTimer);
            this.setCartTimer = null;
        }

        this.callCartListeners();
        this.setCartTimer = setTimeout(() => {
            this.setCartTimer = null;
            this.setCart();
        }, 500);
    }

    setCart = () => {
        FunctionHandler.sendData('/setCart', {cart: this.encodeCart(this.cart)}, (data, err) => {
            if (!err && this.setCartTimer === null) {
                this.storeCartFromBackend(data.cart);
            }
        });
    }

    changeParam = (itemKey, value, paramKey, productId) => {
        if (this.cart?.items?.[itemKey]?.params) {
            const item = Object.assign({}, this.cart.items[itemKey]);
            item.params[paramKey] = value;
            const newItemKey = `${productId}-${hash(item.params)}`
            const existingItemsWithSameType = this.cart.items[newItemKey];

            if (existingItemsWithSameType) {
                existingItemsWithSameType.nr += this.cart.items[itemKey].nr;
            } else {
                this.decorateWithParamPrice(item);
                this.cart.items[newItemKey] = item;
            }

            if (newItemKey !== itemKey) {
                delete this.cart.items[itemKey]
            }

            this.setCartWithTimer();
        }
    }

    /**
     * @param {CartItemContract} cartItem
     */
    addItemToCart = async (cartItem) => {
        const product = await ProductManager.getProduct(cartItem.productId);
        const itemKey = `${cartItem.productId}-${hash(cartItem.params)}`;
        const existingItemsWithSameType = this.cart.items[itemKey];

        if (existingItemsWithSameType) {
            existingItemsWithSameType.nr += cartItem.nr;
        } else {
            this.decorateWithParamPrice(cartItem);
            this.cart.items[itemKey] = cartItem;
        }

        if (product.minPiece) {
            this.cart.items[itemKey].nr = Math.max(product.minPiece, this.cart.items[itemKey].nr);
        }

        this.setCartWithTimer();
    }

    decorateWithParamPrice = (cartItem) => {
        cartItem.paramPrice = '';
        Object.keys(cartItem.params).forEach(paramKey => {
            if (cartItem.params[paramKey]?.hasOwnProperty('price')) {
                cartItem.paramPrice = paramKey;
            }
        });
    }

    /**
     * @param {string} key
     */
    removeItemsFromCart = (key) => {
        delete this.cart.items[key];
        this.setCartWithTimer();
    }

    /**
     * @param {CartItemContract} cartItem
     */
    changeCartItem = (cartItem) => {
        this.cart.items[cartItem.productId] = cartItem;
        this.setCartWithTimer();
    }

    /**
     * @param {string} productId
     * @param {number} nr
     */
    changeCartItemNr = (productId, nr) => {
        this.cart.items[productId].nr = nr;
        this.setCartWithTimer();
    }

    getCart = () => {
        return this.cart;
    }

    getCartId = () => {
        return this.cartId;
    }

    addDesignPath = (path) => {
        this.cart.designs.push(encodeURIComponent(path));
        this.setCartWithTimer();
    }

    addDesignsPath = (paths) => {
        this.cart.designs = this.cart.designs.concat(paths.map(path => encodeURIComponent(path)));
        this.setCartWithTimer();
    }

    selectADesign = (src, nr, size) => {
        this.cart.selectedDesigns[src + size] = {nr, size, src};
        this.setCartWithTimer();
    }

    addToSelectDesigns = (selection, size) => {
        Object.keys(selection).forEach((src) => {
            if (this.cart.selectedDesigns[src + size]) {
                this.cart.selectedDesigns[src + size].nr += selection[src];
            } else {
                this.cart.selectedDesigns[src + size] = {nr: selection[src], size, src};
            }
        })

        this.setCartWithTimer();
    }

    changeSelectedDesignNr = (key, nr) => {
        this.cart.selectedDesigns[key].nr = nr;
        this.setCartWithTimer();
    }

    removeASelectedDesign = (key) => {
        delete this.cart.selectedDesigns[key]
        this.setCartWithTimer();
    }

    changeUserInfo = (userInfo) => {
        this.cart.invoiceData.userInfo = userInfo;
        this.setCartWithTimer();
    }

    getShipping = () => {
        return this.cart?.shipping;
    }

    getInvoiceData = () => {
        return this.cart?.invoiceData;
    }

    handleSaveAddressCheckboxChange = (value) => {
        if (!this.cart?.shipping) {
            this.cart.shipping = new ShipmentContract();
        }

        this.cart.shipping.saveAddress = value;
        this.setCart();
    }

    changeAddress = (address) => {
        this.cart.shipping.submitted = false;
        this.cart.shipping.saveAddress = false;
        this.cart.shipping.address = address;
        this.setCartWithTimer();
    }

    changeShippingType = (type) => {
        this.cart.shipping.submitted = false;
        this.cart.shipping.saveAddress = false;
        this.cart.shipping.type = type;
        this.cart.shipping.addressType = '';
        this.cart.shipping.price = this.shippingPrices[type];
        this.setCart();
    }

    changeInvoiceDataType = (type) => {
        this.cart.invoiceData.type = type;
        this.setCart();
    }

    getShippingType = () => {
        return this.cart?.shipping?.type;
    }

    getPersonType = () => {
        return this.cart?.invoiceData?.type;
    }

    getEasyBox = () => {
        return this.cart?.shipping?.easyBox;
    }

    submitEasyBox = (easyBox) => {
        this.cart.shipping.easyBox = easyBox;
        this.setCart();
    }

    submitAddress = (addressType) => {
        if (!this.cart.shipping.price) {
            this.cart.shipping.price = this.shippingPrices[this.cart.shipping.type];
        }

        this.cart.shipping.submitted = true;
        this.cart.shipping.addressType = addressType;
        this.setCart();
    }

    validateSelectedDesigns = () => {
        let valid = true;
        const totalNecessaryPS = this.getTotalNecessaryDesignsPerSize();
        const totalSelectedPS = this.getTotalNumberOfSelectedDesignPerSize();

        const allSizes = Object.keys(totalNecessaryPS).concat(Object.keys(totalSelectedPS));
        const sizes = allSizes.filter((item, index) => allSizes.indexOf(item) === index);
        sizes.forEach(size => {
            valid = valid && ((totalNecessaryPS[size] || 0) === (totalSelectedPS[size] || 0));
        });


        return valid;
    }

    validateFileParams = () => {
        let valid = true;
        Object.keys(this.cart.items).forEach((key) => {
            if (this.cart.items[key]?.params?.file) {
                const fileParam = this.cart.items[key]?.params?.file;
                valid = valid && fileParam.path && fileParam.ratio && fileParam.width;
            }
        });

        return valid;
    }

    validateShipment = () => {
        return this.cart.shipping.submitted;
    }

    validateInvoiceData = () => {
        const validity = validateInvoiceData(this.cart.invoiceData, UserManager.getUser());
        return Object.keys(validity).length === 0;
    }

    subscribeToCartChange = (listener) => {
        const uuId = getUUId();
        this.cartChangeListeners[uuId] = listener;
        listener(this.cart);
        return () => delete this.cartChangeListeners[uuId];
    }

    hasDisabledItems = () => {
        return Object.keys(this.cart.items).filter(key => this.cart.items[key]?.product?.disabled).length > 0;
    }

    getTotalNumberOfItems = () => {
        return Object.keys(this.cart.items).reduce((acc, key) => this.cart.items[key].nr + acc, 0)
    }

    getTotalNecessaryDesigns = () => {
        return Object.keys(this.cart.items).reduce((acc, key) =>
            (this.cart.items[key].nr * (this.cart.items[key].product?.necessaryDesigns || 0)) + acc, 0)
    }

    getTotalNecessaryDesignsPerSize = () => {
        const obj = {}
        Object.keys(this.cart.items).forEach((key) => {
            if (this.cart.items[key].product?.necessaryDesigns) {
                const size = this.cart.items[key].params?.size?.label || `${this.cart.items[key].params?.size?.width} cm` || 'A3';
                obj[size] = obj[size] || 0;
                obj[size] += this.cart.items[key].nr * this.cart.items[key].product.necessaryDesigns;
            }
        });

        return obj;
    }

    getTotalNumberOfSelectedDesign = () => {
        return Object.keys(this.cart.selectedDesigns).reduce((acc, key) => this.cart.selectedDesigns[key].nr + acc, 0)
    }

    getTotalNumberOfSelectedDesignPerSize = () => {
        const obj = {}

        Object.keys(this.cart.selectedDesigns).forEach((key) => {
            const size = this.cart.selectedDesigns[key].size;
            obj[size] = obj[size] || 0;
            obj[size] += this.cart.selectedDesigns[key].nr;
        });

        return obj;
    }

    static decorateCartWithProducts = async (cart) => {
        let items = {};
        const keys = Object.keys(cart.items);
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const product = await ProductManager.getProduct(cart.items[key].productId);
            items[key] = cart.items[key];
            items[key].product = product;
        }

        return Object.assign({}, cart, {items});
    }

    setPaymentType = (type) => {
        this.cart.paymentType = type;
        this.setCart();
    }

    validatePayment = () => {
        return Boolean(this.cart.paymentType);
    }

    getPaymentType = () => {
        return this.cart.paymentType;
    }

    getShippingPriceForType = (type) => {
        return this.shippingPrices?.[type];
    }

    getShippingPrice = () => {
        return this.cart.shipping.price;
    }

    getActiveStep = () => {
        return this.cart?.activeStep || 0;
    }

    setActiveStep = (activeStep) => {
        this.cart.activeStep = activeStep;
        this.setCart();
    }

    sendOrder = (navigate, langCode, openPopup) => {
        FunctionHandler.sendData('/sendOrder', {
            cartId: this.cart.cartId,
            langCode: langCode
        }, (data, err) => {
            if (!err && data.cardPayment) {
                const {error, payment} = data.cardPayment;

                if (error?.code === '101' && payment?.paymentURL) {
                    document.location = payment.paymentURL;
                } else {
                    FunctionHandler.sendData('/delete/pendingOrder', {orderID: data.nr});
                    openPopup('popup.paymentError');
                }
            } else {
                navigate(`/ordered?orderId=${data.nr}&cashPayment=true`);
            }

            this.loadCart();
        })
    }

    addPromoCode = (code) => {
        this.cart.promoCode = code;
        this.setCart();
    }
}


const CartManager = new _CartManager();


export default CartManager

export function useCartChange() {
    const [cart, setCart] = useState(CartManager.getCart());

    useEffect(() => {
        const unsubscribe = CartManager.subscribeToCartChange(async (nextCart) => {
            const newVar = await _CartManager.decorateCartWithProducts(nextCart);
            setCart(newVar);
        });

        return () => {
            unsubscribe();
        };
    }, []);

    return cart;
}

export function getTotalPriceOfCart(cart) {
    if (cart) {
        let totalPrice = 0;
        Object.keys(cart.items).forEach(key => {
            const item = cart.items[key];
            totalPrice += item.nr * getDiscountedItemPrice(item);
        })

        return totalPrice;
    }

    return 0;
}

export function getDiscountedPrice(cart) {
    const totalPrice = getTotalPriceOfCart(cart);
    if (cart?.discount) {
        return totalPrice * (1 - cart.discount);
    }

    return totalPrice;
}

export function getItemPrice(item) {
    let price = item.product?.price || 0;
    if (item.paramPrice && item.params?.[item.paramPrice] && item.params?.[item.paramPrice].price) {
        price = item.params?.[item.paramPrice].price;
    }

    price = Number(price);

    Object.keys(item.params || {}).forEach(paramKey => {
        if (item.params[paramKey]?.hasOwnProperty('priceModifier')) {
            price = Number(ParamTypes.getPriceModifier(item.product.params.filter(param => param.key === paramKey)[0], item.params[paramKey], price));
        }
    });

    return Math.floor(Number(price) * 100) / 100;
}

export function getDiscountedItemPrice(item) {
    const itemPrice = getItemPrice(item);
    return itemPrice * (1 - (item.discount || 0));
}

export function getTotalItemCount(cart) {
    if (cart) {
        let itemCount = 0;
        Object.keys(cart.items).forEach(key => {
            const item = cart.items[key];
            itemCount += item.nr;
        })

        return itemCount;
    }

    return 0;
}
